import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

// Remix Icons
import { 
    RiUserFollowLine, 
    RiBuilding4Line, 
    RiAwardLine, 
    RiLineChartLine, 
    RiTv2Line, 
    RiTeamLine, 
    RiUserAddLine, 
    RiRefreshLine, 
    RiTimeLine, 
    RiMagicLine 
} from 'react-icons/ri';

export default function AdminDashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchStats = async () => {
        setLoading(true);
        try {
            const res = await axios.get('/api/dashboard/admin');
            if (res.data.success) {
                setData(res.data);
            }
        } catch (err) {
            console.error('Failed to load dashboard stats');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
    }, []);

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto px-4 py-16 text-center text-slate-400">
                <RiRefreshLine className="w-8 h-8 animate-spin mx-auto text-indigo-500 mb-2" />
                <p className="text-xs font-bold">Loading Job Fair Telemetry...</p>
            </div>
        );
    }

    const stats = data?.stats || {};

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
            
            {/* Top Bar Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <span className="text-[11px] font-extrabold uppercase tracking-wider text-indigo-400 bg-indigo-500/10 px-3 py-1 rounded-full border border-indigo-500/20">
                        Admin Control Center
                    </span>
                    <h1 className="text-3xl font-black mt-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
                        Job Fair Overview
                    </h1>
                    <p className="text-xs font-semibold text-slate-400 mt-1">Real-time telemetry, hiring metrics & stall management</p>
                </div>

                <div className="flex items-center space-x-3">
                    <button
                        onClick={fetchStats}
                        className="p-2.5 rounded-xl glass-card text-slate-300 hover:text-indigo-400 transition-all shadow-sm"
                        title="Refresh Stats"
                    >
                        <RiRefreshLine className="w-5 h-5" />
                    </button>

                    <Link
                        to="/big-screen"
                        target="_blank"
                        className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-500 hover:to-pink-500 text-white font-black text-xs shadow-lg shadow-rose-600/30 flex items-center space-x-2 transition-all"
                    >
                        <RiTv2Line className="w-4 h-4 animate-pulse text-white" />
                        <span>Launch Big Screen TV View</span>
                    </Link>
                </div>
            </div>

            {/* Stat Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="glass-card rounded-2xl p-5 border relative overflow-hidden group">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-400">Total Applicants</span>
                        <div className="w-10 h-10 rounded-xl bg-indigo-500/20 text-indigo-400 flex items-center justify-center">
                            <RiUserFollowLine className="w-5 h-5" />
                        </div>
                    </div>
                    <div className="text-3xl font-black mt-2">{stats.total_applicants || 0}</div>
                    <div className="text-[11px] text-slate-400 font-semibold mt-1">Registered for Fair</div>
                </div>

                <div className="glass-card rounded-2xl p-5 border relative overflow-hidden group">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-400">Participating Companies</span>
                        <div className="w-10 h-10 rounded-xl bg-purple-500/20 text-purple-400 flex items-center justify-center">
                            <RiBuilding4Line className="w-5 h-5" />
                        </div>
                    </div>
                    <div className="text-3xl font-black mt-2">{stats.total_companies || 0}</div>
                    <div className="text-[11px] text-slate-400 font-semibold mt-1">Stalls Active</div>
                </div>

                <div className="glass-card rounded-2xl p-5 border relative overflow-hidden group">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-400">Shortlisted / Selected</span>
                        <div className="w-10 h-10 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center">
                            <RiAwardLine className="w-5 h-5" />
                        </div>
                    </div>
                    <div className="text-3xl font-black text-emerald-400 mt-2">{stats.selected_count || 0}</div>
                    <div className="text-[11px] text-emerald-400/80 font-semibold mt-1">Hired / Selected</div>
                </div>

                <div className="glass-card rounded-2xl p-5 border relative overflow-hidden group">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-400">Interviews Conducted</span>
                        <div className="w-10 h-10 rounded-xl bg-cyan-500/20 text-cyan-400 flex items-center justify-center">
                            <RiLineChartLine className="w-5 h-5" />
                        </div>
                    </div>
                    <div className="text-3xl font-black text-cyan-400 mt-2">{stats.visited_count + stats.selected_count + stats.rejected_count}</div>
                    <div className="text-[11px] text-slate-400 font-semibold mt-1">Completed Interviews</div>
                </div>
            </div>

            {/* Quick Action Navigation Buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Link
                    to="/users"
                    className="glass-card p-4 rounded-2xl border hover:border-cyan-500/40 flex items-center space-x-3 transition-all group"
                >
                    <div className="p-3 rounded-xl bg-cyan-500/10 text-cyan-400 group-hover:scale-110 transition-transform">
                        <RiTeamLine className="w-6 h-6" />
                    </div>
                    <div>
                        <div className="font-extrabold text-sm group-hover:text-cyan-400">User Management</div>
                        <div className="text-xs text-slate-400">Create Admin, Coordinator, HR</div>
                    </div>
                </Link>

                <Link
                    to="/companies"
                    className="glass-card p-4 rounded-2xl border hover:border-purple-500/40 flex items-center space-x-3 transition-all group"
                >
                    <div className="p-3 rounded-xl bg-purple-500/10 text-purple-400 group-hover:scale-110 transition-transform">
                        <RiBuilding4Line className="w-6 h-6" />
                    </div>
                    <div>
                        <div className="font-extrabold text-sm group-hover:text-purple-400">Company Registry</div>
                        <div className="text-xs text-slate-400">Add & Tag Employers</div>
                    </div>
                </Link>

                <Link
                    to="/applicants"
                    className="glass-card p-4 rounded-2xl border hover:border-emerald-500/40 flex items-center space-x-3 transition-all group"
                >
                    <div className="p-3 rounded-xl bg-emerald-500/10 text-emerald-400 group-hover:scale-110 transition-transform">
                        <RiUserAddLine className="w-6 h-6" />
                    </div>
                    <div>
                        <div className="font-extrabold text-sm group-hover:text-emerald-400">Applicant Registry</div>
                        <div className="text-xs text-slate-400">Register & Assign Queue</div>
                    </div>
                </Link>
            </div>

            {/* Main Content Grid: Top Hiring Companies & Live Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                
                {/* Left: Top Hiring Companies Leaderboard (2 cols) */}
                <div className="lg:col-span-2 glass-card rounded-3xl p-6 border">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                            <RiMagicLine className="w-5 h-5 text-amber-400" />
                            <h2 className="text-lg font-black">Company Hiring Leaderboard</h2>
                        </div>
                        <span className="text-xs text-slate-400 font-semibold">Ranked by Selected Candidates</span>
                    </div>

                    <div className="space-y-3">
                        {data?.top_companies?.map((company, index) => (
                            <div key={company.id} className="p-4 glass-card rounded-2xl border flex items-center justify-between transition-all">
                                <div className="flex items-center space-x-3">
                                    <span className={`w-7 h-7 rounded-lg flex items-center justify-center font-black text-xs ${
                                        index === 0 ? 'bg-amber-500 text-slate-950 glow-amber' :
                                        index === 1 ? 'bg-slate-300 text-slate-950' :
                                        index === 2 ? 'bg-amber-700 text-white' : 'bg-slate-800 text-slate-400'
                                    }`}>
                                        #{index + 1}
                                    </span>
                                    <div>
                                        <div className="font-extrabold text-sm">{company.company_name}</div>
                                        <div className="text-xs text-slate-400 font-semibold">📍 {company.office_location || 'Indore'} • {company.sector || 'General'}</div>
                                    </div>
                                </div>

                                <div className="flex items-center space-x-4 text-right">
                                    <div>
                                        <div className="text-lg font-black text-emerald-400">{company.selected_count || 0}</div>
                                        <div className="text-[10px] text-slate-400 uppercase font-bold">Selections</div>
                                    </div>
                                    <div className="hidden sm:block">
                                        <div className="text-sm font-bold text-indigo-400">{company.applicant_visits_count || 0}</div>
                                        <div className="text-[10px] text-slate-400 uppercase font-bold">Total Visits</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right: Recent Live Activity Ticker (1 col) */}
                <div className="glass-card rounded-3xl p-6 border">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                            <RiTimeLine className="w-5 h-5 text-indigo-400" />
                            <h2 className="text-lg font-black">Live Activity Feed</h2>
                        </div>
                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping"></span>
                    </div>

                    <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                        {data?.recent_activities?.map((act) => (
                            <div key={act.id} className="p-3.5 glass-card rounded-2xl border text-xs">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="font-extrabold">{act.applicant?.name || 'Applicant'}</span>
                                    <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${
                                        act.status === 'selected' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
                                        act.status === 'rejected' ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30' :
                                        'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30'
                                    }`}>
                                        {act.status}
                                    </span>
                                </div>

                                <div className="text-slate-400 font-semibold text-[11px]">
                                    Company: <span className="text-purple-400 font-bold">{act.company?.company_name}</span>
                                </div>
                                {act.remarks && (
                                    <div className="text-slate-400 text-[10px] italic mt-1 bg-slate-900/40 p-2 rounded-xl border border-slate-800/60">
                                        "{act.remarks}"
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

            </div>
        </div>
    );
}
